import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Download as DlIcon, RefreshCw, LogOut } from "lucide-react";

const CITY_ORDER = ["sydney", "melbourne", "adelaide", "london", "paris"];
const CITY_LABEL = {
  sydney: "Sydney",
  melbourne: "Melbourne",
  adelaide: "Adelaide",
  london: "London",
  paris: "Paris",
};

function toCsv(rows) {
  const headers = [
    "ticket_reference",
    "city",
    "date",
    "quantity",
    "amount",
    "currency",
    "customer_name",
    "customer_email",
    "paid_at",
    "session_id",
  ];
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const lines = [headers.join(",")];
  for (const r of rows) {
    lines.push(headers.map((h) => escape(r[h])).join(","));
  }
  return lines.join("\n");
}

export default function AdminTickets() {
  const [key, setKey] = useState(() => {
    try {
      return localStorage.getItem("metamorph_admin_key") || "";
    } catch (e) { return ""; }
  });
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [filterCity, setFilterCity] = useState("all");

  const load = async (adminKey) => {
    setLoading(true);
    try {
      const res = await api.get("/admin/tickets", {
        headers: { "X-Admin-Key": adminKey },
      });
      setData(res.data);
      setAuthed(true);
      try { localStorage.setItem("metamorph_admin_key", adminKey); } catch (e) { /* ignore */ }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        toast.error("Incorrect admin key.");
        setAuthed(false);
      } else {
        toast.error("Could not load tickets.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSignIn = (e) => {
    e.preventDefault();
    if (!key.trim()) {
      toast.error("Enter the admin key.");
      return;
    }
    load(key.trim());
  };

  const onSignOut = () => {
    try { localStorage.removeItem("metamorph_admin_key"); } catch (e) { /* ignore */ }
    setKey("");
    setAuthed(false);
    setData(null);
  };

  const exportCsv = () => {
    if (!data?.rows) return;
    const filtered = filterCity === "all" ? data.rows : data.rows.filter((r) => r.city === filterCity);
    const csv = toCsv(filtered);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `metamorph-tickets-${filterCity}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const rows = (data?.rows || []).filter((r) => filterCity === "all" || r.city === filterCity);
  const summary = data?.summary || {};
  const totalTickets = CITY_ORDER.reduce((acc, c) => acc + (summary[c]?.tickets || 0), 0);
  const totalRevenue = CITY_ORDER.reduce((acc, c) => acc + (summary[c]?.revenue || 0), 0);

  if (!authed) {
    return (
      <main className="min-h-screen bg-black text-white flex flex-col px-6 py-10" data-testid="admin-gate">
        <header className="mb-16">
          <Link to="/" className="vintage-link text-xs sm:text-sm uppercase" data-testid="admin-back">
            ← Back
          </Link>
        </header>
        <div className="flex-1 flex items-center justify-center">
          <form onSubmit={onSignIn} className="w-full max-w-sm">
            <h1 className="vintage-text text-4xl sm:text-5xl mb-2 text-center" data-testid="admin-title">
              Admin
            </h1>
            <p className="vintage-link text-[10px] uppercase tracking-[0.22em] text-center opacity-70 mb-10">
              Tickets & buyers
            </p>
            <input
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="Admin key"
              className="line-input"
              autoFocus
              data-testid="admin-key-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="btn-line btn-filled w-full justify-center mt-6"
              data-testid="admin-signin"
            >
              {loading ? "Checking…" : "Enter"}
            </button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-black text-white px-6 sm:px-10 py-8" data-testid="admin-dashboard">
      <header className="flex items-center justify-between mb-10">
        <Link to="/" className="vintage-link text-xs uppercase" data-testid="admin-back">
          ← Metamorph
        </Link>
        <h1 className="vintage-text text-2xl sm:text-3xl" data-testid="admin-heading">Tickets</h1>
        <div className="flex items-center gap-3">
          <Link to="/admin/scan" className="vintage-link text-xs uppercase flex items-center gap-2" data-testid="admin-to-scan">
            Scan →
          </Link>
          <button onClick={() => load(key)} className="vintage-link text-xs uppercase flex items-center gap-2" data-testid="admin-refresh">
            <RefreshCw className="w-3 h-3" strokeWidth={1.2} /> Refresh
          </button>
          <button onClick={onSignOut} className="vintage-link text-xs uppercase flex items-center gap-2 opacity-70" data-testid="admin-signout">
            <LogOut className="w-3 h-3" strokeWidth={1.2} /> Sign out
          </button>
        </div>
      </header>

      {/* Summary */}
      <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8" data-testid="admin-summary">
        {CITY_ORDER.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setFilterCity(filterCity === c ? "all" : c)}
            className={`border p-4 text-left transition-colors ${filterCity === c ? "border-white/60" : "border-white/15 hover:border-white/40"}`}
            data-testid={`admin-summary-${c}`}
          >
            <p className="vintage-link text-[10px] uppercase tracking-[0.22em] opacity-70 mb-1">{CITY_LABEL[c]}</p>
            <p className="vintage-text text-2xl">{summary[c]?.tickets || 0}</p>
            <p className="font-mono text-[10px] text-white/55 mt-1">${(summary[c]?.revenue || 0).toFixed(2)}</p>
          </button>
        ))}
        <div className="border border-white/30 p-4 bg-white/[0.02]">
          <p className="vintage-link text-[10px] uppercase tracking-[0.22em] opacity-90 mb-1">Total</p>
          <p className="vintage-text text-2xl" data-testid="admin-total-tickets">{totalTickets}</p>
          <p className="font-mono text-[10px] text-white/65 mt-1" data-testid="admin-total-revenue">
            ${totalRevenue.toFixed(2)}
          </p>
        </div>
      </section>

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="vintage-link text-[10px] uppercase tracking-[0.22em] opacity-70">Filter</span>
          <select
            value={filterCity}
            onChange={(e) => setFilterCity(e.target.value)}
            className="bg-transparent border border-white/20 px-2 py-1 text-xs uppercase tracking-[0.16em]"
            data-testid="admin-filter"
          >
            <option value="all">All cities</option>
            {CITY_ORDER.map((c) => (
              <option key={c} value={c}>{CITY_LABEL[c]}</option>
            ))}
          </select>
        </div>
        <button onClick={exportCsv} className="vintage-link text-xs uppercase flex items-center gap-2" data-testid="admin-export">
          <DlIcon className="w-3 h-3" strokeWidth={1.2} /> Export CSV
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto border border-white/10">
        <table className="w-full text-sm" data-testid="admin-table">
          <thead>
            <tr className="border-b border-white/15 text-left">
              <th className="px-3 py-3 vintage-link text-[10px] uppercase tracking-[0.18em] opacity-65">Ref</th>
              <th className="px-3 py-3 vintage-link text-[10px] uppercase tracking-[0.18em] opacity-65">City</th>
              <th className="px-3 py-3 vintage-link text-[10px] uppercase tracking-[0.18em] opacity-65">Date</th>
              <th className="px-3 py-3 vintage-link text-[10px] uppercase tracking-[0.18em] opacity-65">Name</th>
              <th className="px-3 py-3 vintage-link text-[10px] uppercase tracking-[0.18em] opacity-65">Email</th>
              <th className="px-3 py-3 vintage-link text-[10px] uppercase tracking-[0.18em] opacity-65 text-right">Qty</th>
              <th className="px-3 py-3 vintage-link text-[10px] uppercase tracking-[0.18em] opacity-65 text-right">Total</th>
              <th className="px-3 py-3 vintage-link text-[10px] uppercase tracking-[0.18em] opacity-65">Paid at</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-white/40" data-testid="admin-empty">
                  No paid tickets yet.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.session_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="px-3 py-3 font-mono text-xs whitespace-nowrap">{r.ticket_reference || "—"}</td>
                  <td className="px-3 py-3">{r.city_label || r.city}</td>
                  <td className="px-3 py-3 text-white/65">{r.date || "—"}</td>
                  <td className="px-3 py-3">{r.customer_name || "—"}</td>
                  <td className="px-3 py-3 text-white/75">{r.customer_email || "—"}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{r.quantity}</td>
                  <td className="px-3 py-3 text-right font-mono text-xs tabular-nums">
                    ${Number(r.amount || 0).toFixed(2)}
                  </td>
                  <td className="px-3 py-3 text-white/55 text-xs whitespace-nowrap">
                    {r.paid_at ? new Date(r.paid_at).toLocaleString() : "—"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </main>
  );
}
